"use client";

import {
  Identify,
  identify,
  init,
  reset,
  setOptOut,
  setUserId,
  track,
} from "@amplitude/analytics-browser";
import { isProductionTelemetryEnabled } from "../telemetry";
import { captureSourceAttribution, getAttributionProperties } from "./attribution";

let amplitudeReady = false;

type AmplitudePropertyValue = string | number | boolean;

function getPagePath(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return `${window.location.pathname}${window.location.search}`;
}

function getLocale(path = getPagePath()): string | undefined {
  return path?.split("/")[1] || undefined;
}

export function getAmplitudeEventContext(): Record<string, string | number> {
  if (typeof window === "undefined") {
    return {
      app: "tutuly",
      env: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || "development",
      event_ts: Date.now(),
    };
  }

  captureSourceAttribution();
  const pagePath = getPagePath();

  return {
    app: "tutuly",
    env: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || "development",
    event_ts: Date.now(),
    ...(pagePath ? { page_path: pagePath } : {}),
    ...(window.location.href ? { page_location: window.location.href } : {}),
    ...(globalThis.document?.title ? { page_title: globalThis.document.title } : {}),
    ...(globalThis.document?.referrer ? { referrer: globalThis.document.referrer } : {}),
    ...(getLocale(pagePath) ? { locale: getLocale(pagePath) } : {}),
    ...getAttributionProperties(),
  };
}

export function toAmplitudeProperties(
  properties: Record<string, unknown>,
): Record<string, AmplitudePropertyValue> {
  const filtered: Record<string, AmplitudePropertyValue> = {};
  const seen = new WeakSet<object>();

  function addProperty(key: string, value: unknown): void {
    if (!key || value === undefined || value === null) {
      return;
    }

    if (value instanceof Date) {
      filtered[key] = value.toISOString();
      return;
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      filtered[key] = value;
    }
  }

  function visit(prefix: string, value: unknown, depth = 0): void {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value !== "object" || value instanceof Date) {
      addProperty(prefix, value);
      return;
    }

    if (seen.has(value)) {
      return;
    }
    seen.add(value);

    if (depth >= 3) {
      try {
        filtered[`${prefix}_json`] = JSON.stringify(value);
      } catch {
        // Ignore circular or non-serializable deep payloads; scalar leaves above still survive.
      }
      return;
    }

    if (Array.isArray(value)) {
      if (value.every((item) => typeof item !== "object" || item === null)) {
        filtered[prefix] = value.filter((item) => item !== undefined && item !== null).join(",");
        return;
      }

      value.forEach((item, index) => visit(`${prefix}_${index}`, item, depth + 1));
      return;
    }

    for (const [nestedKey, nestedValue] of Object.entries(value)) {
      visit(`${prefix}_${nestedKey}`, nestedValue, depth + 1);
    }
  }

  for (const [key, value] of Object.entries(properties)) {
    visit(key, value);
  }

  return filtered;
}

function getAmplitudeApiKey(): string | undefined {
  const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY?.trim();
  return apiKey ? apiKey : undefined;
}

export function initAmplitude(): void {
  const apiKey = getAmplitudeApiKey();
  if (
    !(isProductionTelemetryEnabled() && apiKey) ||
    amplitudeReady ||
    typeof window === "undefined"
  ) {
    return;
  }

  init(apiKey, {
    autocapture: false,
    defaultTracking: false,
  });
  amplitudeReady = true;
}

export function setAmplitudeConsent(enabled: boolean): void {
  if (!(isProductionTelemetryEnabled() && getAmplitudeApiKey())) {
    return;
  }

  if (enabled) {
    initAmplitude();
  }

  setOptOut(!enabled);
}

export function trackAmplitudeEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  if (!(isProductionTelemetryEnabled() && amplitudeReady)) {
    return;
  }

  track(
    eventName,
    toAmplitudeProperties({
      ...getAmplitudeEventContext(),
      ...properties,
    }),
  );
}

export function trackAmplitudePageView(
  path: string,
  title: string,
  additionalProperties: Record<string, unknown> = {},
): void {
  if (!isProductionTelemetryEnabled()) {
    return;
  }

  trackAmplitudeEvent("page_view", {
    page_path: path,
    page_title: title,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    ...additionalProperties,
  });
}

export function identifyAmplitudeUser(
  userId: string,
  properties: Record<string, unknown> = {},
): void {
  if (!(isProductionTelemetryEnabled() && amplitudeReady)) {
    return;
  }

  setUserId(userId);
  const filtered = toAmplitudeProperties(properties);
  if (Object.keys(filtered).length === 0) {
    return;
  }

  const userIdentify = new Identify();
  for (const [key, value] of Object.entries(filtered)) {
    userIdentify.set(key, value);
  }
  identify(userIdentify);
}

export function setAmplitudeUserProperties(properties: Record<string, unknown>): void {
  const filtered = toAmplitudeProperties(properties);
  if (!(isProductionTelemetryEnabled() && amplitudeReady) || Object.keys(filtered).length === 0) {
    return;
  }

  const userIdentify = new Identify();
  for (const [key, value] of Object.entries(filtered)) {
    userIdentify.set(key, value);
  }
  identify(userIdentify);
}

export function resetAmplitudeUser(): void {
  if (!(isProductionTelemetryEnabled() && amplitudeReady)) {
    return;
  }

  reset();
}
